import { useEffect, useCallback, useState, useRef } from "react";
import {
  Autocomplete,
  Box,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import parse from "autosuggest-highlight/parse";
import { BCPLocationDot } from "../icons/bcp/BCPIcons";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { googleTimezoneApi } from "./UtilityFunctions";

const libraries = ["places"];
const mapContainerStyle = {
  width: "100%",
  height: "50vh",
};

const options = {
  disableDefaultUI: true,
  zoomControl: true,
};

export default function GoogleMapsSearchBar(props) {
  const { locationData, setLocationData, initialSearch } = props;
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: getEnvironmentVariable("GOOGLE_API_KEY"),
    libraries,
  });

  const [marker, setMarker] = useState(null);
  const [center, setCenter] = useState({
    lat: 41.850033,
    lng: -87.6500523,
  });
  const [setSearchValue, setSetSearchValue] = useState(null);

  const mapRef = useRef();
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const panTo = useCallback(({ lat, lng }) => {
    setCenter({ lat, lng });

    if (mapRef.current) {
      mapRef.current.panTo({ lat, lng });
      mapRef.current.setZoom(13);
    }
  }, []);

  const handleMapClick = useCallback(
    async (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      try {
        panTo({ lat, lng });
        setMarker({ lat, lng });

        const results = await getGeocode({ location: { lat, lng } });
        const timezone = await googleTimezoneApi({
          location: `${lat},${lng}`,
          key: getEnvironmentVariable("GOOGLE_API_KEY")
        });

        var loc = {
          coordinate: [lng, lat]
        };

        if (timezone?.timeZoneId) loc.timeZone = timezone.timeZoneId;
        if (results[0]?.address_components) {
          results[0].address_components.forEach(
            ({ long_name, short_name, types }) => {
              if (types.includes("subpremise")) loc.subPremise = short_name;
              if (types.includes("street_number")) loc.streetNum = short_name;
              if (types.includes("route")) loc.streetName = long_name;
              if (types.includes("locality")) loc.city = long_name;
              if (types.includes("administrative_area_level_1"))
                loc.state = short_name;
              if (types.includes("country")) loc.country = long_name;
              if (types.includes("postal_code")) loc.zip = short_name;
            }
          );
        }

        loc.formatted_address = results[0]?.formatted_address;

        setLocationData(loc);

        if (setSearchValue && loc.formatted_address) {
          setSearchValue(loc.formatted_address, false);
        }
      } catch (error) {
        console.error("Error reverse geocoding location: ", error);
      }
    },
    [panTo, setLocationData, setMarker, setSearchValue]
  );

  useEffect(() => {
    if (!isLoaded) return;
    if (locationData?.coordinate) {
      panTo({
        lat: locationData.coordinate[1],
        lng: locationData.coordinate[0],
      });
      setMarker({
        lat: locationData.coordinate[1],
        lng: locationData.coordinate[0],
      });
    } else {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            panTo({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          () => null
        );
      }
    }
    // eslint-disable-next-line
  }, [props, isLoaded]);

  if (loadError) return "Error loading maps";
  if (!isLoaded) return "Loading Maps";

  return (
    <Box sx={{ position: "relative" }}>
      <Search
        panTo={panTo}
        center={center}
        initialSearch={initialSearch}
        setMarker={setMarker}
        setLocationData={setLocationData}
        setSetSearchValue={setSetSearchValue}
      />
      <GoogleMap
        id="map"
        mapContainerStyle={mapContainerStyle}
        zoom={13}
        center={center}
        options={options}
        onLoad={onMapLoad}
        onClick={handleMapClick}
      >
        {marker && <Marker position={{ lat: marker.lat, lng: marker.lng }} />}
      </GoogleMap>
    </Box>
  );
}

function Search({
  panTo,
  center,
  initialSearch,
  setMarker,
  setLocationData,
  setSetSearchValue
}) {
  const {
    value,
    suggestions: { data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      location: {
        lat: () => center.lat,
        lng: () => center.lng,
      },
      radius: 200 * 1000,
    },
  });
  const theme = useTheme();

  const handleInput = (e) => {
    if (e?.target?.value) {
      setValue(e.target.value);
    }
  };

  const handleSelect = async (event, address) => {
    setValue(address, false);
    clearSuggestions();

    try {
      const results = await getGeocode({ address: address?.description });
      const result = await getLatLng(results[0]);
      const { lat, lng } = result;
      panTo({ lat, lng });
      setMarker({ lat, lng });
      const timezone = await googleTimezoneApi({
        location: `${lat},${lng}`,
        key: getEnvironmentVariable("GOOGLE_API_KEY"),
      });

      var loc = {
        coordinate: [lng, lat],
      };

      if (timezone?.timeZoneId) loc.timeZone = timezone.timeZoneId;
      if (results[0]?.address_components) {
        results[0].address_components.forEach(
          ({ long_name, short_name, types }) => {
            if (types.includes("subpremise")) loc.subPremise = short_name;
            if (types.includes("street_number")) loc.streetNum = short_name;
            if (types.includes("route")) loc.streetName = long_name;
            if (types.includes("locality")) loc.city = long_name;
            if (types.includes("administrative_area_level_1"))
              loc.state = short_name;
            if (types.includes("country")) loc.country = long_name;
            if (types.includes("postal_code")) loc.zip = short_name;
          }
        );
      }

      if (address?.structured_formatting?.main_text) {
        const addressLines = [address.structured_formatting.main_text];
        if (address?.structured_formatting?.secondary_text) {
          let line2 = address.structured_formatting.secondary_text;
          if (loc.zip && address.terms?.length > 2) {
            let country = address.terms.pop();
            let state = address.terms.pop();
            line2 = line2.replace(
              `${state?.value}, ${country?.value}`,
              `${state?.value} ${loc.zip}, ${country?.value}`
            );
          }
          addressLines.push(line2.trim());
        }
        loc.formatted_address = addressLines.join(", ");
      } else {
        loc.formatted_address = results[0]?.formatted_address;
      }

      setLocationData(loc);
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  useEffect(() => {
    setValue(initialSearch, false);
    // eslint-disable-next-line
  }, [initialSearch]);

  useEffect(() => {
    if (setSetSearchValue) {
      setSetSearchValue(() => setValue);
    }
  }, [setValue, setSetSearchValue]);

  return (
    <Box
      sx={{
        mb: 1,

        "& input": {
          border: "0",
          background: "none",
          height: "unset !important",
          lineHeight: "21px",
          margin: "0",
          display: "block",
          animationDuration: "10ms",
          padding: "16.5px 14px !important",

          "&:hover": {
            borderColor: `${theme?.palette?.text?.primary} !important`,
          },
          "&:focus-visible": {
            borderColor: `${theme?.palette?.secondary?.main} !important`,
            outlineColor: `${theme?.palette?.secondary?.main}`,
          },
        },
      }}
    >
      <Autocomplete
        id="googleMapSearchBox"
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.description
        }
        disablePortal
        filterOptions={(x) => x}
        options={data}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={value}
        noOptionsText="No locations"
        onChange={handleSelect}
        onInputChange={handleInput}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            margin="normal"
            label="Search Location Address"
            fullWidth
            sx={{ "& .MuiOutlinedInput-root": { padding: "unset" } }}
          />
        )}
        renderOption={(props, option) => {
          const matches =
            option.structured_formatting.main_text_matched_substrings || [];

          const parts = parse(
            option.structured_formatting.main_text,
            matches.map((match) => [match.offset, match.offset + match.length])
          );

          return (
            <li {...props}>
              <Grid container alignItems="center">
                <Grid item sx={{ display: "flex", width: 44 }}>
                  <BCPLocationDot
                    style={{ color: theme?.palette?.text?.secondary }}
                  />
                </Grid>
                <Grid
                  item
                  sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}
                >
                  {parts.map((part, index) => (
                    <Box
                      key={index}
                      component="span"
                      sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                    >
                      {part.text}
                    </Box>
                  ))}

                  <Typography variant="body2" color="text.secondary">
                    {option.structured_formatting.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
    </Box>
  );
}
